// src/pages/SignUpPage.jsx
import React, { useState } from 'react';
import { auth, db } from '../firebase';
import { createUserWithEmailAndPassword, updateProfile, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react'; // <--- Import Icons

const SignUpPage = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'student' 
  });
  
  // --- VISIBILITY STATE ---
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;
      const fullName = `${formData.firstName} ${formData.lastName}`;

      await updateProfile(user, { displayName: fullName });
      await sendEmailVerification(user);

      const referralCode = searchParams.get('ref') || localStorage.getItem('aulice_ref_id');

      await setDoc(doc(db, 'users', user.uid), {
        firstName: formData.firstName,
        lastName: formData.lastName,
        displayName: fullName,
        email: formData.email,
        role: formData.role,
        createdAt: serverTimestamp(),
        emailVerified: false,
        referredBy: referralCode || null,
      });

      alert(`Account created! A verification email has been sent to ${formData.email}.`);
      
      if (formData.role === 'partner') navigate('/partner-dashboard');
      else if (formData.role === 'teacher') navigate('/teacher-dashboard');
      else navigate('/my-dashboard');

    } catch (err) {
      console.error(err);
      const cleanError = err.message.replace('Firebase:', '').replace('auth/', '').replace(/-/g, ' ');
      setError(cleanError.charAt(0).toUpperCase() + cleanError.slice(1));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-xl shadow-lg">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Join the Guild
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Start your professional mastery journey
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSignUp}>
          {error && <div className="bg-red-100 text-red-700 p-3 rounded text-sm text-center">{error}</div>}
          
          <div className="rounded-md shadow-sm space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <input name="firstName" type="text" required className="appearance-none rounded relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="First Name" onChange={handleChange} />
              <input name="lastName" type="text" required className="appearance-none rounded relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="Last Name" onChange={handleChange} />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">I am joining as a:</label>
              <select 
                name="role" 
                value={formData.role} 
                onChange={handleChange} 
                className="block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                <option value="student">Student (Learner)</option>
                <option value="partner">Partner (Affiliate)</option>
                <option value="teacher">Teacher (Mentor)</option>
              </select>
            </div>

            <input name="email" type="email" required className="appearance-none rounded relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="Email address" onChange={handleChange} />
            
            {/* PASSWORD FIELD WITH EYE ICON */}
            <div className="relative">
              <input 
                name="password" 
                type={showPassword ? "text" : "password"} // Toggles type
                required 
                className="appearance-none rounded block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm pr-10" 
                placeholder="Password" 
                onChange={handleChange} 
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-indigo-600 focus:outline-none"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {/* CONFIRM PASSWORD FIELD WITH EYE ICON */}
            <div className="relative">
              <input 
                name="confirmPassword" 
                type={showConfirmPassword ? "text" : "password"} // Toggles type
                required 
                className="appearance-none rounded block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm pr-10" 
                placeholder="Confirm Password" 
                onChange={handleChange} 
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-indigo-600 focus:outline-none"
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

          </div>

          <div>
            <button type="submit" disabled={loading} className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
              {loading ? 'Processing...' : 'Sign Up'}
            </button>
          </div>
        </form>
        <div className="text-center text-sm">
          <Link to="/login" className="text-indigo-600 hover:text-indigo-500">Already have an account? Log in</Link>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;